import React, { useEffect, useState } from "react";
import { AlertTriangle, Bell, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { SendArrow, sendToDispatchWithPrompt } from "./CardReadiness";

/** SCH-1 §2 — Schedule Alerts (replaces the Schedule Pool). A list, not a pool: every scope whose next
 *  start is within 21 days or past, plus any scope with no material on hand starting within 7 days.
 *  Past due first (most overdue at top), then soonest; priority first inside its band. */
const SYMBOL = {
  red: ["text-red-600", "Past due, or no material on hand with the start within 7 days"],
  amber: ["text-amber-600", "Starts within 7 days — material on hand"],
  grey: ["text-muted-foreground", "No material on hand — start more than 7 days out"],
};
const fmt = (iso) => (iso ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}` : "—");

export default function ScheduleAlerts({ onOpenMob, onDragMob, onChanged, mobsById = {} }) {
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState([]);
  const load = async () => {
    try {
      const [a, n] = await Promise.all([api.get("/schedule-alerts"), api.get("/notifications").catch(() => ({ data: [] }))]);
      setData(a.data); setNotes(n.data || []);
    } catch { setData({ rows: [] }); }
  };
  useEffect(() => { load(); window.addEventListener("schedule-alerts:invalidate", load); return () => window.removeEventListener("schedule-alerts:invalidate", load); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const rows = data?.rows || [];
  return (
    <div className="flex flex-col min-h-0" data-testid="schedule-alerts">
      <header className="px-3 py-2 border-b border-border bg-muted/40">
        <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" /> Schedule Alerts
          {notes.length > 0 && <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-amber-700 dark:text-amber-300" title={notes.map((n) => n.text).join("\n")} data-testid="schedule-alerts-notifications"><Bell className="w-3 h-3" /> {notes.length}</span>}
        </h3>
        <p className="text-[10px] text-muted-foreground" data-testid="schedule-alerts-count">
          {data ? `${rows.length} scope${rows.length === 1 ? "" : "s"} within 21 days or past due` : "loading…"}
          {data?.auto_sent?.length ? ` · ${data.auto_sent.length} auto-sent` : ""}
        </p>
      </header>
      <div className="flex-1 overflow-y-auto divide-y divide-border">
        {!data && <div className="p-4 text-center"><Loader2 className="w-4 h-4 animate-spin inline text-muted-foreground" /></div>}
        {data && rows.length === 0 && <div className="text-[11px] text-muted-foreground text-center py-6" data-testid="schedule-alerts-empty">Nothing starting in the next 21 days.</div>}
        {rows.map((r) => {
          const [cls, why] = SYMBOL[r.symbol] || SYMBOL.grey;
          const mob = r.mob_id ? (mobsById[r.mob_id] || { id: r.mob_id }) : null;
          const draggable = !!mob && r.mob_status === "requested";
          return (
            <div key={`${r.project_id}-${r.scope}`} data-testid={`alert-row-${r.project_id}-${r.scope}`}
                 data-symbol={r.symbol} data-past={r.past ? "1" : "0"}
                 draggable={draggable}
                 onDragStart={draggable ? () => onDragMob?.(r.mob_id) : undefined}
                 onDragEnd={draggable ? () => onDragMob?.(null) : undefined}
                 onClick={() => mob && onOpenMob?.(mob)}
                 title={`${why}${mob ? "" : " · no mobilization yet — request one from the project"}`}
                 className={`px-2.5 py-1.5 text-[11px] flex items-center gap-2 ${mob ? "cursor-pointer hover:bg-muted/50" : "opacity-80"} ${draggable ? "cursor-grab" : ""}`}>
              <AlertTriangle className={`w-3.5 h-3.5 shrink-0 ${cls}`} data-testid={`alert-symbol-${r.project_id}-${r.scope}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 min-w-0">
                  {r.priority && <span className="text-[9px] font-semibold uppercase rounded bg-foreground text-background px-1" title={r.priority_reason} data-testid="alert-priority-chip">Priority</span>}
                  <span className="font-medium text-foreground truncate">{r.job_number ? `${r.job_number} · ` : ""}{r.project_name}</span>
                </div>
                <div className="text-muted-foreground flex items-center gap-1.5">
                  <span className="font-semibold text-foreground">{r.scope}</span>
                  <span className={r.past ? "text-red-600 font-semibold" : ""}>{fmt(r.start)} → {fmt(r.finish)}</span>
                  {r.in_dispatch && <span className="text-[9px] uppercase rounded border border-border px-1" title="Already in the dispatch pool">dispatch</span>}
                  {r.review_flag && <AlertTriangle className="w-3 h-3 text-amber-600" title={`Flagged: ${r.review_flag.reason}`} />}
                </div>
              </div>
              {mob && r.mob_status === "scheduled" && (
                <SendArrow testId={`alert-send-${r.mob_id}`} onClick={() => sendToDispatchWithPrompt([r.mob_id], () => { load(); onChanged?.(); })} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
